import React, { useMemo, useState } from "react";

import { Box, Textarea } from "@chakra-ui/react";
import { freezeOrUnfreezeSubAccount } from "api/business-user-management";
import { IFreezeOrUnfreezeSubAccountPayload } from "api/business-user-management/types";
import { ReactComponent as InfoOutlineIcon } from "assets/icons/info-outline.svg";
import { customStyledSelectStyles } from "features/businesses/add-new-business/styles";
import { useUploadDocument } from "hooks/useUploadDocument";
import { REGEX_VALUES, getUniqueFileName } from "utils";
import { Maybe } from "utils/general/maybe";

import { Button } from "ui/button";
import { customErrorToast, customSuccessToast } from "ui/custom-toast";
import { FileUpload } from "ui/file-upload";
import { Modal } from "ui/modal";
import { ModalHeader } from "ui/modal/modal-header";
import { defaultModalCss } from "ui/modal/styles";
import { StyledSelect } from "ui/styled-select";
import { IStyledSelectOption } from "ui/styled-select/types";
import { Heading3, Paragraph3 } from "ui/typography";

import {
  DEFAULT_FREEZE_UNFREEZE_FORM_DATA,
  MIN_REASON_LENGTH,
  OPTIONS_FOR_REASON_TO_ACTIVATE_OR_UNFREEZE,
} from "../../consts";
import {
  IFreezeUnfreezeBusinessAccountFormData,
  IRemovePostNoDebitModalProps,
} from "./types";

export const RemovePostNoDebitModal = (props: IRemovePostNoDebitModalProps) => {
  const {
    isOpen,
    setIsOpen,
    handleToggleRemovePostNoDebitModal,
    walletId,
    handleGetBusinessAccounts,
  } = props;
  const { isUploading, uploadFile } = useUploadDocument();
  const [isRemovingPostNoDebit, setIsRemovingPostNoDebit] = useState(false);
  const [formData, setFormData] =
    useState<IFreezeUnfreezeBusinessAccountFormData>(
      DEFAULT_FREEZE_UNFREEZE_FORM_DATA
    );
  const [othersReason, setOthersReason] = useState("");
  const [isSelectMenuOpen, setIsSelectMenuOpen] = useState(false);

  const isFormDisabled = useMemo(() => {
    let isReasonDisabled = false;
    if (
      (formData.reason === "Others" &&
        othersReason.length < MIN_REASON_LENGTH) ||
      formData.reason.length < MIN_REASON_LENGTH
    ) {
      isReasonDisabled = true;
    }

    return (
      isRemovingPostNoDebit ||
      !formData.attachment ||
      isUploading ||
      isReasonDisabled
    );
  }, [isRemovingPostNoDebit, isUploading, formData, othersReason]);

  const handleCloseModal = () => {
    setFormData(DEFAULT_FREEZE_UNFREEZE_FORM_DATA);
    setOthersReason("");
    handleToggleRemovePostNoDebitModal();
  };

  const handleReasonChange = (evt: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = evt.target;
    if (!REGEX_VALUES.LETTERS_WITH_NUMBERS_COMMA_AND_HYPHEN_ONLY.test(value)) {
      return;
    }
    setOthersReason(value);
  };

  const handleSelectChange = (value: unknown, name?: string) => {
    const selectedValue = value as IStyledSelectOption;
    if (!name) {
      console.error("Error: Select input needs a name attribute!");
      return;
    }
    setFormData({
      ...formData,
      [name]: selectedValue.value,
    });
  };

  const handleFileChange = (name: string, file: File | null) => {
    setFormData({
      ...formData,
      [name]: file,
    });
  };

  const handleRemovePostNoDebit = async () => {
    const { attachment } = formData;
    const reason =
      formData.reason === "Others" ? othersReason : formData.reason;
    const payload: IFreezeOrUnfreezeSubAccountPayload = {
      walletId,
      isFreeze: false,
      reason,
    };
    setIsRemovingPostNoDebit(true);
    try {
      const {
        status,
        url,
        message: singleFileUploadMsg,
      } = await uploadFile({
        file: attachment as File,
        documentName: "others",
        companyName: getUniqueFileName(
          `${walletId}-unfreeze-business-account-attachment`
        ),
      });
      if (status === "failed") {
        customErrorToast({
          message: singleFileUploadMsg,
        });
        setIsRemovingPostNoDebit(false);
        return;
      }
      payload["attachment"] = url;
      const { error, message } = await freezeOrUnfreezeSubAccount(payload);
      if (error) {
        customErrorToast({
          message,
        });
      } else {
        customSuccessToast({
          message: "Unfreeze successful",
        });
        await handleGetBusinessAccounts({});
        handleCloseModal();
      }
    } catch (error) {
      console.error("Error: ", error);
    }
    setIsRemovingPostNoDebit(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      modalCss={{
        ...defaultModalCss,
        width: "400px",
        maxHeight: "650px",
        padding: "24px 0",
        gap: "32px",
      }}
      allowOutsideClick={false}
    >
      <Box className="flex flex-col gap-5">
        <ModalHeader
          modalType="approval"
          modalHeaderCss={{
            marginBottom: 0,
            paddingLeft: "24px",
            paddingRight: "24px",
          }}
        />
        <Box
          className={`px-6 flex flex-col gap-5 flex-shrink-0 overflow-x-hidden overflow-y-scroll ${!isSelectMenuOpen ? "h-[390px]" : "h-[480px]"}`}
        >
          <Box className="flex flex-col gap-2">
            <Heading3
              fontSize="18px"
              lineHeight="28px"
              textColor="#1A202C"
              fontWeight="600"
            >
              Unfreeze
            </Heading3>
            <Paragraph3
              lineHeight="20px"
              textColor="#475467"
            >
              Please confirm that you want to perform this action.
            </Paragraph3>
            <Box className="bg-[#FEF5E7] border border-[#FFEFD8] rounded-2xl p-3 flex items-center gap-2 h-16">
              <InfoOutlineIcon className="flex-shrink-0" />
              <Paragraph3
                lineHeight="20px"
                textColor=" #1A202C"
              >
                This account will now be able to make transactions
              </Paragraph3>
            </Box>
          </Box>
          <Box>
            <StyledSelect
              options={OPTIONS_FOR_REASON_TO_ACTIVATE_OR_UNFREEZE}
              name="reason"
              value={
                formData.reason
                  ? {
                      label: formData.reason,
                      value: formData.reason,
                    }
                  : null
              }
              handleOnChange={handleSelectChange}
              label="Reason for Unfreeze"
              labelClassName="!font-semibold !leading-[20px] !text-[14px] !text-[#101828]"
              selectWrapperClassName="w-full"
              placeholder="Select a reason"
              customStyles={customStyledSelectStyles}
              menuPlacement="auto"
              setIsMenuOpen={setIsSelectMenuOpen}
            />
            <Maybe condition={formData.reason === "Others"}>
              <Box className="h-[179px] flex flex-col gap-[6px] mt-2">
                <Textarea
                  css={{
                    border: "1px solid #D0D5DD",
                    boxShadow: "box-shadow: 0px 1px 2px 0px #1018280D",
                    borderRadius: "8px",
                    height: "173px",
                    padding: "10px 16px",
                    fontSize: "14px",
                    resize: "none",

                    "&::placeholder": {
                      color: "#B1B9CA",
                    },
                  }}
                  placeholder="Enter your reason..."
                  onChange={handleReasonChange}
                  value={othersReason}
                />
              </Box>
            </Maybe>
          </Box>
          <Box>
            <FileUpload
              fileUploadLabel="Attachment"
              file={formData.attachment}
              name="attachment"
              handleFileChange={handleFileChange}
              doesUploadedFileContainerHaveFullWidth
              fileTypes={[
                "jpeg",
                "jpg",
                "png",
                "pdf",
                "doc",
                "docx",
                "xls",
                "xlsx",
                "csv",
              ]}
              uploadFileEmptyStateContainerCss={{
                height: "118px",
                flexShrink: "0",
              }}
              fileUploadLabelCss={{
                fontSize: "14px",
              }}
            />
          </Box>
        </Box>
      </Box>
      <Box className="flex items-center gap-3 px-6 h-[44px]">
        <Button
          variant="outline"
          textColor="#344054"
          border="1px solid #D0D5DD"
          className="w-[170px]"
          onClick={handleCloseModal}
          disabled={isRemovingPostNoDebit || isUploading}
        >
          Cancel
        </Button>
        <Button
          variant="solid"
          textColor="#ffffff"
          border="1px solid #00535C"
          className="w-[170px]"
          onClick={handleRemovePostNoDebit}
          disabled={isFormDisabled}
          loading={isRemovingPostNoDebit || isUploading}
          loadingText="Unfreezing..."
        >
          Unfreeze
        </Button>
      </Box>
    </Modal>
  );
};
